import { Controller, type Control, type FieldErrors, type UseFormRegister } from 'react-hook-form';
import { Lock } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { FieldLabel } from '@/components/forms/FieldLabel';
import { TextField } from '@/components/forms/TextField';
import { TextareaField } from '@/components/forms/TextareaField';
import { PhoneField } from '@/components/forms/PhoneField';
import { useProfileCountryIso2 } from '@/hooks/useProfileCountryIso2';
import { toPhoneFormFields } from '@/lib/utils/phone.utils';
import { ContactPreferenceField } from './ContactPreferenceField';
import { PARTNER_PROFILE_STRINGS as S } from './partner-profile.strings';
import type { PartnerProfileData } from '../../api/partner-profile.api';
import type { PartnerProfileFormValues } from './schema';

interface IdentitySectionProps {
  data: PartnerProfileData;
  control: Control<PartnerProfileFormValues>;
  register: UseFormRegister<PartnerProfileFormValues>;
  errors?: FieldErrors<PartnerProfileFormValues>;
}

/**
 * The About panel's fields: business, contact, email, phone, website,
 * scheduling, contact preference and bio (FR-28). The read views are
 * `PartnerAboutCard` and `PartnerContactCard` (MYN-217).
 */
export function IdentitySection({ data, control, register, errors }: IdentitySectionProps) {
  // The picker starts on the person's own country when known (FR-184), else the first in the list.
  const defaultCountryIso2 = useProfileCountryIso2();
  return (
    <Card title={S.IDENTITY} headerDivider hoverEffect={false}>
      <div className="flex flex-col gap-4">
        <TextField
          {...register('businessName')}
          label={S.BUSINESS_NAME}
          labelVariant="caps"
          required
          error={errors?.businessName?.message}
        />
        {/* Short values two to a row; the contact choice and the bio keep the full width. */}
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            {...register('contactName')}
            label={S.CONTACT_NAME}
            labelVariant="caps"
            required
            error={errors?.contactName?.message}
          />
          <TextField
            {...register('contactTitle')}
            label={S.CONTACT_TITLE}
            labelVariant="caps"
            required
            error={errors?.contactTitle?.message}
          />
          {/* Locked to the caller's own account email (matches
            investor/IdentitySection.tsx's identical pattern) - never a form
            field. `contactEmail` is deliberately absent from
            `UpdatePartnerProfileDto`/`partnerProfileSchema`: the Backend
            seeds it from the authenticated user on first creation only and
            never overwrites it (see partner-profile.service.ts). Do not
            re-add a `register('contactEmail')` TextField here - that was
            tried and reverted (MYN-102/104): it required the client to
            submit a value nothing populated, and now the Backend's
            `forbidNonWhitelisted` ValidationPipe rejects `contactEmail` in
            the PATCH body outright. */}
          <div className="flex flex-col gap-1.5">
            <FieldLabel label={S.EMAIL} variant="caps" />
            <Input
              label={S.EMAIL}
              value={data.contactEmail ?? ''}
              disabled
              readOnly
              onChange={() => {}}
              rightIcon={<Lock size={12} aria-hidden="true" />}
              className="bg-page-bg"
            />
            <p className="text-p6 text-slate pl-1 italic">{S.EMAIL_LOCKED_HINT}</p>
          </div>
          <Controller
            control={control}
            name="phone"
            render={({ field }) => (
              <PhoneField
                {...field}
                label={S.PHONE}
                required
                defaultCountryIso2={defaultCountryIso2}
                legacyText={toPhoneFormFields(data.phone).legacyPhone}
                error={errors?.phone?.message}
              />
            )}
          />
          <TextField
            {...register('websiteUrl')}
            label={S.WEBSITE}
            labelVariant="caps"
            required
            type="url"
            error={errors?.websiteUrl?.message}
          />
          <TextField
            {...register('schedulingUrl')}
            label={S.SCHEDULING}
            labelVariant="caps"
            type="url"
            error={errors?.schedulingUrl?.message}
            helpText={S.SCHEDULING_HINT}
          />
        </div>
        <ContactPreferenceField control={control} error={errors?.contactPreference?.message} />
        <TextareaField
          {...register('description')}
          label={S.BIO}
          labelVariant="caps"
          required
          rows={4}
          error={errors?.description?.message}
        />
      </div>
    </Card>
  );
}
